import { useState } from 'react';
import { deserialize, serialize } from 'serializr';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import { generatePath } from 'react-router-dom';
import { LocationData } from 'models/ActivityDataEntryScopes/locationData.model';
import { ScopeThreeData } from 'models/ActivityDataEntryScopes/scopeThree.model';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

const LocationService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [scopeLoading, setScopeLoading] = useState<boolean>(false);
  const [yearLoading, setYearLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteLocationloading, setDeleteLocationLoading] = useState<boolean>(false);
  const [location, setLocation] = useState<Location>();
  const [locationScopeDetails, setLocationScopeDetails] = useState<LocationData>();
  const [scopeThreeData, setScopeThreeData] = useState<ScopeThreeData>();

  const fetchLocation = async (locationId?: number) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        generatePath(ApiRoutes.GET_LOCATION, { locationId })
      );
      const data = deserialize(Location, response.data['data']) as Location;
      setLocation(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const createYearForLocation = async (locationId?: string, year?: string) => {
    try {
      setYearLoading(true);
      const response = await axiosInstance.post(
        generatePath(ApiRoutes.LOCATION_DETAILS, {
          location_id: locationId,
          year,
        })
      );
      Notification({
        message: response?.data?.message ?? `Location activated for year ${year}`,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
    } finally {
      setYearLoading(false);
    }
  };

  const fetchYearForLocation = async (location_id?: string, year?: string) => {
    try {
      setScopeLoading(true);
      const response = await axiosInstance.get(
        generatePath(ApiRoutes.LOCATION_DETAILS, { location_id, year })
      );
      const desiredData = deserialize(LocationData, response.data);
      const scopeThreeData = deserialize(
        ScopeThreeData,
        response.data?.waste_treatment
      );
      setLocationScopeDetails(desiredData);
      setScopeThreeData(scopeThreeData as ScopeThreeData);
    } catch (error) {
    } finally {
      setScopeLoading(false);
    }
  };

  const updateLocation = async (locationId?: number, payload?: Location) => {
    try {
      setLoading(true);
      const payloadWithoutStatus = {
        ...payload,
        yearsWithScopeStatus: payload?.yearsWithScopeStatus?.map(
          ({ status, ...rest }) => rest
        ),
      };
      const desiredPayload = serialize(
        Location,
        payloadWithoutStatus as Location
      );
      const response = await axiosInstance.put(
        generatePath(ApiRoutes.GET_LOCATION, { locationId }),
        desiredPayload
      );
      Notification({
        message: response?.data?.message ?? 'Location details updated',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const deleteLocation = async (locationId?: number) => {
    try {
      setDeleteLocationLoading(true);
      const response = await axiosInstance.delete(
        generatePath(ApiRoutes.GET_LOCATION, { locationId })
      );
      Notification({
        message: response?.data?.message,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setDeleteLocationLoading(false);
    }
  };

  const deleteYearForLocation = async (locationId: string, year: number) => {
    try {
      setDeleteLoading(true);
      const response = await axiosInstance.delete(
        generatePath(ApiRoutes.LOCATION_DETAILS, {
          location_id: locationId,
          year,
        })
      );
      Notification({
        message: response?.data?.message,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    loading,
    scopeLoading,
    yearLoading,
    deleteLoading,
    deleteLocationloading,
    location,
    locationScopeDetails,
    scopeThreeData,
    fetchLocation,
    createYearForLocation,
    fetchYearForLocation,
    updateLocation,
    deleteLocation,
    deleteYearForLocation,
  };
};

export default LocationService; 